"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { Card, CardContent } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";
import { EmptyState } from "@/components/ui/EmptyState";
import { AIAssistantModal } from "@/components/ui/AIAssistantModal";
import { formatCurrency } from "@/lib/utils";

interface WishListItem {
  id: string;
  description: string | null;
  notes: string | null;
  priority: number;
  targetDate: string | null;
  priceRangeLow: number | null;
  priceRangeHigh: number | null;
  createdAt: string;
  customer: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    photoUrl: string | null;
    isVip: boolean;
  };
  jewelryItem: {
    id: string;
    name: string;
    sku: string;
    photoUrls: string[];
    retailPrice: number;
    status: string;
    category: string;
  } | null;
}

const FILTERS: Array<{ value: "ALL" | "IN_STOCK" | "VIP" | "HIGH_PRIORITY"; label: string }> = [
  { value: "ALL", label: "All" },
  { value: "IN_STOCK", label: "In stock now" },
  { value: "VIP", label: "VIP clients" },
  { value: "HIGH_PRIORITY", label: "High priority" },
];

function initials(a?: string | null, b?: string | null) {
  return [a?.[0], b?.[0]].filter(Boolean).join("").toUpperCase();
}

export default function WishlistsPage() {
  const [items, setItems] = useState<WishListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"ALL" | "IN_STOCK" | "VIP" | "HIGH_PRIORITY">("ALL");
  const [modal, setModal] = useState<null | { entity: string; example: string }>(null);

  useEffect(() => {
    fetch("/api/wishlists")
      .then((r) => r.json())
      .then((d) => setItems(d.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (filter === "IN_STOCK") return items.filter((i) => i.jewelryItem?.status === "IN_STOCK");
    if (filter === "VIP") return items.filter((i) => i.customer.isVip);
    if (filter === "HIGH_PRIORITY") return items.filter((i) => i.priority >= 3);
    return items;
  }, [items, filter]);

  const stats = useMemo(() => {
    const inStock = items.filter((i) => i.jewelryItem?.status === "IN_STOCK").length;
    const pipelineValue = items.reduce((s, i) => s + (i.jewelryItem?.retailPrice || i.priceRangeHigh || 0), 0);
    const uniqueClients = new Set(items.map((i) => i.customer.id)).size;
    return { total: items.length, inStock, pipelineValue, uniqueClients };
  }, [items]);

  return (
    <div className="space-y-6 animate-fade-in">
      <AIAssistantModal
        isOpen={modal !== null}
        onClose={() => setModal(null)}
        entity={modal?.entity || "action"}
        example={modal?.example}
      />
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-violet-300/30 to-indigo-500/10">
              <svg className="h-4 w-4 text-violet-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <h1 className="text-2xl font-semibold text-white">Wish Lists</h1>
            <span className="ml-1 rounded-full border border-amber-200/30 bg-amber-200/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-200/80">
              AI matching
            </span>
          </div>
          <p className="mt-1 text-sm text-muted">
            What your clients want — matched to live inventory for instant outreach.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card><CardContent className="py-4">
          <p className="text-xs uppercase tracking-wider text-text-dim">Total wishes</p>
          <p className="mt-1 text-xl font-semibold text-white">{stats.total}</p>
        </CardContent></Card>
        <Card><CardContent className="py-4">
          <p className="text-xs uppercase tracking-wider text-text-dim">In stock now</p>
          <p className="mt-1 text-xl font-semibold text-emerald-200">{stats.inStock}</p>
        </CardContent></Card>
        <Card><CardContent className="py-4">
          <p className="text-xs uppercase tracking-wider text-text-dim">Unique clients</p>
          <p className="mt-1 text-xl font-semibold text-white">{stats.uniqueClients}</p>
        </CardContent></Card>
        <Card><CardContent className="py-4">
          <p className="text-xs uppercase tracking-wider text-text-dim">Pipeline value</p>
          <p className="mt-1 text-xl font-semibold text-white">{formatCurrency(stats.pipelineValue)}</p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => {
          const active = filter === f.value;
          return (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition ${
                active
                  ? "border-violet-300/40 bg-violet-300/10 text-violet-200"
                  : "border-white/10 bg-white/5 text-white/60 hover:border-white/20 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No wishes yet"
          description="Save client wishes from the customer page or add freeform wishes to track interest."
          icon={
            <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((w) => {
            const item = w.jewelryItem;
            const fullName = [w.customer.firstName, w.customer.lastName].filter(Boolean).join(" ");
            const inStock = item?.status === "IN_STOCK";
            return (
              <Card key={w.id} className="overflow-hidden transition hover:border-white/20">
                {item?.photoUrls?.[0] ? (
                  <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-white/5 to-transparent">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={item.photoUrls[0]} alt={item.name} className="h-full w-full object-cover" />
                    {inStock && (
                      <div className="absolute left-2 top-2">
                        <Badge variant="success" size="sm">In stock</Badge>
                      </div>
                    )}
                    {w.priority >= 3 && (
                      <div className="absolute right-2 top-2 rounded-full border border-amber-200/30 bg-black/60 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-200 backdrop-blur">
                        Priority
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex aspect-video items-center justify-center bg-gradient-to-br from-violet-500/10 to-indigo-500/5 text-white/30">
                    <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                  </div>
                )}
                <CardContent className="space-y-3 py-4">
                  <div>
                    <h3 className="text-sm font-semibold text-white">
                      {item?.name || w.description || "Custom wish"}
                    </h3>
                    {item && (
                      <p className="text-xs text-text-dim">{item.sku} · {formatCurrency(item.retailPrice)}</p>
                    )}
                    {!item && (w.priceRangeLow || w.priceRangeHigh) && (
                      <p className="text-xs text-text-dim">
                        Budget: {w.priceRangeLow ? formatCurrency(w.priceRangeLow) : ""}
                        {w.priceRangeLow && w.priceRangeHigh ? " – " : ""}
                        {w.priceRangeHigh ? formatCurrency(w.priceRangeHigh) : ""}
                      </p>
                    )}
                  </div>
                  {w.notes && (
                    <p className="text-xs text-white/60 italic">{w.notes}</p>
                  )}
                  <div className="flex items-center justify-between gap-2 border-t border-white/5 pt-3">
                    <Link
                      href={`/dashboard/customers/${w.customer.id}`}
                      className="flex min-w-0 items-center gap-2 transition hover:text-white"
                    >
                      {w.customer.photoUrl ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={w.customer.photoUrl} alt={fullName} className="h-7 w-7 rounded-full object-cover" />
                      ) : (
                        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-amber-200 to-amber-400 text-[10px] font-semibold text-black">
                          {initials(w.customer.firstName, w.customer.lastName) || "?"}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="truncate text-xs font-medium text-white">{fullName}</p>
                        {w.customer.isVip && (
                          <p className="text-[10px] font-medium uppercase tracking-wider text-amber-200">VIP</p>
                        )}
                      </div>
                    </Link>
                    {inStock ? (
                      <button
                        type="button"
                        onClick={() => setModal({ entity: "client text", example: `Text ${w.customer.firstName || "the client"} a preview of ${w.jewelryItem?.name || "their wishlist item"}.` })}
                        className="rounded-lg bg-amber-200/10 px-3 py-1.5 text-xs font-medium text-amber-200 transition hover:bg-amber-200/20"
                      >
                        Text preview
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setModal({ entity: "sourcing request", example: `Source a ${w.description || "wishlist piece"} for ${w.customer.firstName || "the client"}.` })}
                        className="rounded-lg bg-white/5 px-3 py-1.5 text-xs font-medium text-white/70 transition hover:bg-white/10"
                      >
                        Source
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
